/**
 * MDM-MOBILE-P0-001: Client-Side Telemetry SDK
 *
 * Event buffering (50 events or 30s interval), auto-flush on page visibility change,
 * IndexedDB retry for failed flushes, session context initialization,
 * device context capture, consent checking, HMAC device fingerprint hashing.
 *
 * Target: <5KB gzipped
 */

import { API_BASE } from '../config/api.config.js';
// V-NAV-DEVICE R2: the SAME canonical classifier the vault presentation gate uses, so the UI and
// the analytics record cannot disagree about what device this is.
import { isMobileDevice } from '../utils/deviceClass';

// --- Constants ---
const BUFFER_MAX = 50;
const FLUSH_INTERVAL_MS = 30_000;
const INDEXEDDB_NAME = 'mdm_telemetry';
const INDEXEDDB_STORE = 'retry_queue';
const INDEXEDDB_VERSION = 1;

// --- Module state ---
let _initialized = false;
let _sessionId = null;
let _deviceContext = null;
let _eventBuffer = [];
let _sequenceNumber = 0;
let _flushTimer = null;
let _consent = true; // Essential telemetry enabled by default
let _screensVisited = [];
let _sessionStartTime = null;

// --- Device fingerprint hashing (HMAC-SHA256 via SubtleCrypto) ---
async function hmacSha256(key, message) {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(message));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function sha256(message) {
  const enc = new TextEncoder();
  const hash = await crypto.subtle.digest('SHA-256', enc.encode(message));
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// --- Device context capture ---
/**
 * ⛔ VIEWPORT WIDTH, NOT THE DEVICE. This is the ORIGINAL classifier and it is kept EXACTLY as it
 * is: `device_type` is a closed `Literal["mobile_s","mobile_l","tablet","tablet_l","desktop"]` on
 * the wire (backend schemas/telemetry.py:58), a NOT-NULL indexed column, and eight frontend call
 * sites read it. Changing its vocabulary would 422 at the schema and break stored analytics.
 *
 * ⚠ IT MISLABELS A NARROW DESKTOP WINDOW AS 'mobile_l'. That is exactly why presentation must NOT
 * be gated on it -- see `device_class` below, which is the UA-based answer and the one the vault
 * entry point (utils/vaultNav.js) uses. Two fields, one classifier each, neither guessing at the
 * other's job.
 */
function getDeviceType(width) {
  if (width < 414) return 'mobile_s';
  if (width < 768) return 'mobile_l';
  if (width < 1024) return 'tablet';
  if (width < 1280) return 'tablet_l';
  return 'desktop';
}

function parseUA() {
  const ua = navigator.userAgent;
  let os = 'Unknown';
  let osVersion = null;
  let browser = 'Unknown';
  let browserVersion = null;

  // OS detection
  if (/Windows NT (\d+\.\d+)/.test(ua)) {
    os = 'Windows';
    osVersion = RegExp.$1;
  } else if (/Mac OS X (\d+[._]\d+)/.test(ua)) {
    os = 'macOS';
    osVersion = RegExp.$1.replace(/_/g, '.');
  } else if (/Android (\d+\.?\d*)/.test(ua)) {
    os = 'Android';
    osVersion = RegExp.$1;
  } else if (/iPhone OS (\d+_\d+)/.test(ua)) {
    os = 'iOS';
    osVersion = RegExp.$1.replace(/_/g, '.');
  } else if (/Linux/.test(ua)) {
    os = 'Linux';
  }

  // Browser detection
  if (/Chrome\/(\d+\.\d+)/.test(ua) && !/Edg\//.test(ua)) {
    browser = 'Chrome';
    browserVersion = RegExp.$1;
  } else if (/Firefox\/(\d+\.\d+)/.test(ua)) {
    browser = 'Firefox';
    browserVersion = RegExp.$1;
  } else if (/Safari\/(\d+\.\d+)/.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
    browserVersion = RegExp.$1;
  } else if (/Edg\/(\d+\.\d+)/.test(ua)) {
    browser = 'Edge';
    browserVersion = RegExp.$1;
  }

  return { os, osVersion, browser, browserVersion };
}

/* c8 ignore start */
async function captureDeviceContext() {
  const { os, osVersion, browser, browserVersion } = parseUA();
  /* c8 ignore next */
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  let cameraAvailable = null;
  if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      cameraAvailable = devices.some((d) => d.kind === 'videoinput');
    } catch {
      // Permission denied or unavailable
    }
  }

  let storageQuota = null;
  let storageUsage = null;
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      /* c8 ignore next */
      storageQuota = estimate.quota || null;
      /* c8 ignore next */
      storageUsage = estimate.usage || null;
    } catch {
      // Unavailable
    }
  }

  return {
    device_type: getDeviceType(window.innerWidth),
    // ⭐ ONE DEVICE TRUTH. `device_class` comes from the SAME `isMobileDevice()` the vault
    // presentation gate calls (utils/deviceClass.js: UA Client Hints primary, UA string only as a
    // fallback), so analytics and the UI can never disagree about what this device is. It sits
    // BESIDE the width-based `device_type` rather than replacing it -- see getDeviceType.
    //
    // ⚠ The backend currently DROPS this field: DeviceContext (schemas/telemetry.py:55) declares
    // no model_config and therefore inherits Pydantic's default extra="ignore" -- no 422, no
    // storage. Persisting it is a separate mid-beta PR (additive column + migration).
    device_class: isMobileDevice() ? 'mobile' : 'desktop',
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    /* c8 ignore next */
    device_pixel_ratio: window.devicePixelRatio || 1,
    /* c8 ignore next */
    touch_capable: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    /* c8 ignore next */
    max_touch_points: navigator.maxTouchPoints || 0,
    os,
    os_version: osVersion,
    browser,
    browser_version: browserVersion,
    /* c8 ignore next 3 */
    connection_type: conn?.effectiveType || null,
    connection_downlink: conn?.downlink || null,
    connection_rtt: conn?.rtt || null,
    camera_available: cameraAvailable,
    storage_quota_bytes: storageQuota,
    storage_usage_bytes: storageUsage,
  };
}
/* c8 ignore stop */

// --- UUID v4 generation ---
function uuidv4() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
  );
}

// --- IndexedDB retry queue ---
function openRetryDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(INDEXEDDB_NAME, INDEXEDDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(INDEXEDDB_STORE)) {
        db.createObjectStore(INDEXEDDB_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function enqueueRetry(batch) {
  try {
    const db = await openRetryDB();
    const tx = db.transaction(INDEXEDDB_STORE, 'readwrite');
    tx.objectStore(INDEXEDDB_STORE).add({ batch, timestamp: Date.now() });
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });
  } catch {
    // IndexedDB unavailable — drop silently
  }
}

async function drainRetryQueue() {
  try {
    const db = await openRetryDB();
    const tx = db.transaction(INDEXEDDB_STORE, 'readwrite');
    const store = tx.objectStore(INDEXEDDB_STORE);
    const items = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      /* c8 ignore next */
      req.onerror = () => reject(req.error);
    });

    for (const item of items) {
      try {
        await sendBatch(item.batch);
        store.delete(item.id);
      } catch {
        // Leave in queue for next attempt
        break;
      }
    }
  } catch {
    // IndexedDB unavailable
  }
}

// --- Network send ---
async function sendBatch(payload) {
  const resp = await fetch(`${API_BASE}/v1/telemetry/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!resp.ok) {
    throw new Error(`Telemetry flush failed: ${resp.status}`);
  }
  return resp.json();
}

// --- Core API ---

/**
 * Initialize the telemetry SDK. Call once at app startup.
 */
export async function initTelemetry() {
  if (_initialized) return;

  _sessionId = uuidv4();
  _sessionStartTime = Date.now();
  _sequenceNumber = 0;
  _screensVisited = [];

  _deviceContext = await captureDeviceContext();
  _initialized = true;

  // Emit session.init
  trackEvent('session.init', 'session', {
    session_id: _sessionId,
    device_context: _deviceContext,
  });

  // Start periodic flush
  _flushTimer = setInterval(() => flushEvents(), FLUSH_INTERVAL_MS);

  // Flush on visibility change (tab hide / close)
  document.addEventListener('visibilitychange', _onVisibilityChange);
  window.addEventListener('beforeunload', _onBeforeUnload);

  // Drain any IndexedDB retry queue
  drainRetryQueue();
}

function _onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    _emitSessionSummary();
    flushEvents();
  }
}

function _onBeforeUnload() {
  _emitSessionSummary();
  flushEvents();
}

/* c8 ignore start */
function _emitSessionSummary() {
  if (!_initialized) return;
  const duration = Math.round((Date.now() - _sessionStartTime) / 1000);
  trackEvent('nav.session.summary', 'navigation', {
    screens: [..._screensVisited],
    duration_seconds: duration,
    actions_taken: _sequenceNumber,
    device_type: _deviceContext?.device_type || 'desktop',
  });
}
/* c8 ignore stop */

/**
 * Track a telemetry event.
 * @param {string} name - Event name (e.g., 'nav.screen.viewed')
 * @param {string} domain - Event domain (e.g., 'navigation')
 * @param {object} payload - Event payload
 */
export function trackEvent(name, domain, payload = {}) {
  if (!_initialized || !_consent) return;

  _sequenceNumber++;

  _eventBuffer.push({
    event_name: name,
    event_domain: domain,
    payload,
    client_timestamp: new Date().toISOString(),
    sequence_number: _sequenceNumber,
  });

  // Track screen views for session summary
  if (name === 'nav.screen.viewed' && payload.screen_name) {
    _screensVisited.push(payload.screen_name);
  }

  // Auto-flush when buffer is full
  if (_eventBuffer.length >= BUFFER_MAX) {
    flushEvents();
  }
}

/**
 * Flush buffered events to the server.
 */
export async function flushEvents() {
  if (_eventBuffer.length === 0) return;

  const batch = {
    session_id: _sessionId,
    device_context: _deviceContext,
    events: [..._eventBuffer],
  };
  _eventBuffer = [];

  try {
    await sendBatch(batch);
  } catch {
    // Network failure — queue for retry via IndexedDB
    await enqueueRetry(batch);
  }
}

/**
 * Set consent state. When false, no events are tracked.
 * @param {boolean} value
 */
export function setConsent(value) {
  _consent = Boolean(value);
}

/**
 * Get current session ID.
 */
export function getSessionId() {
  return _sessionId;
}

/**
 * Get current device context.
 */
export function getDeviceContext() {
  return _deviceContext;
}

/**
 * Cleanup telemetry SDK (for unmount).
 */
export function destroyTelemetry() {
  if (_flushTimer) {
    clearInterval(_flushTimer);
    _flushTimer = null;
  }
  document.removeEventListener('visibilitychange', _onVisibilityChange);
  window.removeEventListener('beforeunload', _onBeforeUnload);
  flushEvents();
  _initialized = false;
}

// Export for testing
export { getDeviceType, hmacSha256, sha256, BUFFER_MAX, FLUSH_INTERVAL_MS };
